import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Row } from '../models/ui/table';
import { Entry, Key, KeysOfType } from '../types/shared';

export function isNumber(value) {
  return typeof value === 'number' && isFinite(value);
}

export function duplicate(obj) {
  return JSON.parse(JSON.stringify(obj));
}

export function isEmptyObj(obj) {
  return obj && Object.keys(obj).length === 0 && Object.getPrototypeOf(obj) === Object.prototype;
}

export function getStammValue<T>(
  rows: readonly { DBField: string; Bezeichnung: T }[] | null | undefined,
  dbField: string
): T | undefined {
  return rows?.find(row => row.DBField === dbField)?.Bezeichnung;
}

export function getSpezValue<T>(
  rows: readonly { objekt: string; bezeichnung: T }[] | null | undefined,
  objekt: string
): T | undefined {
  return rows?.find(row => row.objekt === objekt)?.bezeichnung;
}

export function getRandomInt(max: number) {
  return Math.floor(Math.random() * max);
}

export function rgbToHex(rgb: number): string {
  return `#${rgb.toString(16).padEnd(6, '0')}`;
}

export function adjustBrightness(color, amount) {
  return (
    '#' +
    color
      .replace(/^#/, '')
      .replace(/../g, color => ('0' + Math.min(255, Math.max(0, parseInt(color, 16) + amount)).toString(16)).substr(-2))
  );
}
export function LinearShade(p, c) {
  var i = parseInt,
    r = Math.round,
    [a, b, c, d] = c.split(',');
  var P = p < 0;
  var t = P ? 0 : 255 * p;
  var Px = P ? 1 + p : 1 - p;
  var res =
    'rgb' +
    (d ? 'a(' : '(') +
    r(i(a[3] == 'a' ? a.slice(5) : a.slice(4)) * Px + t) +
    ',' +
    r(i(b) * Px + t) +
    ',' +
    r(i(c) * Px + t) +
    (d ? ',' + d : ')');
  return res;
}

export function stringToUint(str) {
  var string = btoa(encodeURIComponent(str)),
    charList = string.split(''),
    uintArray = [];
  for (var i = 0; i < charList.length; i++) {
    uintArray.push(charList[i].charCodeAt(0));
  }
  return new Uint8Array(uintArray);
}

export function uintToString(uintArray) {
  var encodedString = String.fromCharCode.apply(null, uintArray),
    decodedString = decodeURIComponent(escape(atob(encodedString)));
  return decodedString;
}

export function getByteArray(file) {
  let fileReader = new FileReader();
  return new Promise(function (resolve, reject) {
    fileReader.readAsArrayBuffer(file);
    fileReader.onload = function (ev) {
      const array = new Uint8Array(<ArrayBuffer>ev.target.result);
      const fileByteArray = [];
      for (let i = 0; i < array.length; i++) {
        fileByteArray.push(array[i]);
      }
      resolve(fileByteArray); // successful
    };
    fileReader.onerror = reject; // call reject if error
  });
}

export function isEqual(obj1, obj2) {
  var props1 = Object.getOwnPropertyNames(obj1);
  var props2 = Object.getOwnPropertyNames(obj2);
  if (props1.length != props2.length) {
    return false;
  }
  for (var i = 0; i < props1.length; i++) {
    let val1 = obj1[props1[i]];
    let val2 = obj2[props1[i]];
    let isObjects = isObject(val1) && isObject(val2);
    if ((isObjects && !isEqual(val1, val2)) || (!isObjects && val1 !== val2)) {
      return false;
    }
  }
  return true;
}
function isObject(object) {
  return object != null && typeof object === 'object';
}

export function arrayCompare(_arr1, _arr2) {
  if (!Array.isArray(_arr1) || !Array.isArray(_arr2) || _arr1.length !== _arr2.length) {
    return false;
  }

  // .concat() to not mutate arguments
  const arr1 = _arr1.concat().sort();
  const arr2 = _arr2.concat().sort();

  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) {
      return false;
    }
  }

  return true;
}

const unitAbbreviationMap: { [key: string]: string } = {
  Millimeter: 'mm',
  Inch: 'in',
  Meter: 'm',
  Miles: 'mi',
  Feet: 'ft',
  'Mile Feet': 'mileft',
};

const unitConversionMap: { [key: string]: number } = {
  mm: 1,
  in: 25.4,
  m: 1000,
  mi: 1609344,
  ft: 304.8,
  mileft: 1609344,
};

export const measurementUnitItems: string[] = ['Millimeter', 'Inch'];
export const lengthUnitItems: string[] = ['Meter', 'Miles', 'Feet'];

export function addUnitToValue(value: number, unitType: number, unitTypeValue: number): string {
  let unit = getUnit(unitType, unitTypeValue);
  return `${value} ${unitAbbreviationMap[unit]}`;
}

export function addUnitAbbreviationToText(text: string, unitType: number, unitTypeValue: number, withoutBraces: boolean = false): string {
  let unit = getUnit(unitType, unitTypeValue);
  return withoutBraces ? `${text}  ${unitAbbreviationMap[unit]}` : `${text} [${unitAbbreviationMap[unit]}]`;
}

export function addUnitToText(text: string, unitType: number, unitTypeValue: number): string {
  let unit = getUnit(unitType, unitTypeValue);
  return `${text} [${unit}]`;
}
export const lengthUnitColumns = ['leistungslänge', `gleislänge`, 'startmeter', 'endmeter', 'länge'];
export const measurementUnitColumns = ['schienenhöhe', 'überhöhung', 'spurweite', 'dämmung', 'radius'];

export const UnitLength = 1;
export const UnitMeasurement = 0;
function getUnit(unitType: number, unitTypeValue: number) {
  let unit: string;
  if (unitType === 0) {
    unit = measurementUnitItems[unitTypeValue] ?? measurementUnitItems[0];
  } else {
    unit = lengthUnitItems[unitTypeValue] ?? lengthUnitItems[0];
  }
  return unit;
}

export function convertValueToUnit(
  value: number,
  unitType: number,
  fromUnitTypeValue: number,
  toUnitTypeValue: number,
  addUnitToValue: boolean
): string {
  let fromUnit = getUnit(unitType, fromUnitTypeValue);
  let toUnit = getUnit(unitType, toUnitTypeValue);
  const fromConversionFactor = unitConversionMap[unitAbbreviationMap[fromUnit]];
  const toConversionFactor = unitConversionMap[unitAbbreviationMap[toUnit]];
  let tofixedlenght = 2;
  if (toUnit == 'Miles') {
    tofixedlenght = 5;
  }

  if (fromConversionFactor && toConversionFactor) {
    const finalValue =
      Number.isFinite(value) ?
        ((value * fromConversionFactor) / toConversionFactor).toFixed(tofixedlenght).replace('.', ',')
      : '';
    //let t = addUnitToValue ? this.addUnitToValue(finalValue, fromUnit, toUnit) : finalValue;
    return addUnitToValue ? this.addUnitToValue(finalValue, fromUnit, toUnit) : finalValue;
  } else {
    throw new Error('Invalid unit provided');
  }
}

export function mapObject<T extends object, R extends object = T>(
  obj: T,
  mappingKeysFn: ([key, value]: Entry<T>) => Entry<Row>
): R {
  return Object.fromEntries(
    Object.entries(obj)
      .map(mappingKeysFn)
      .filter(x => x !== null)
  ) as R;
}

export async function mapObjectAsync<T extends object, R extends object = T>(
  obj: T,
  mappingKeysFn: ([key, value]: Entry<T>) => Promise<Entry<Row>>
): Promise<R> {
  return Object.fromEntries((await Promise.all(Object.entries(obj).map(mappingKeysFn))).filter(x => x !== null)) as R;
}

export function exhaustiveGuard(value: never): never {
  throw new Error(`Unexpected Value: ${JSON.stringify(value)}`);
}

export function formatBytes(bytes: number) {
  const marker = 1024;
  const decimal = 1;
  const kiloBytes = marker;
  const megaBytes = marker * marker;
  const gigaBytes = marker * marker * marker;

  // return bytes if less than a KB
  if (bytes < kiloBytes) return bytes + ' Bytes';
  // return KB if less than a MB
  else if (bytes < megaBytes) return (bytes / kiloBytes).toFixed(decimal) + ' KB';
  // return MB if less than a GB
  else if (bytes < gigaBytes) return (bytes / megaBytes).toFixed(decimal) + ' MB';
  // return GB if less than a TB
  else return (bytes / gigaBytes).toFixed(decimal) + ' GB';
}
export function getTokenRight(rights: any[], key: string) {
  return rights?.find(r => r.key === key)?.value;
}

export function intersectionBy<T extends object>(array1: T[], array2: T[], key: Key<T>) {
  if (!array1?.length || !array2?.length) return [];

  const array1ByKey = array1.map(item => item[key]);
  return array2.filter(item => array1ByKey.includes(item[key]));
}

export function unionBySort<T extends object>(array1: T[], array2: T[], key: Key<T>) {
  return sortBy([...array1, ...differenceBy(array2, array1, key)], key);
}

export function sortBy<T extends object>(array: T[], key: Key<T>) {
  return array.sort((a, b) => {
    switch (typeof a[key]) {
      case 'number':
        return a[key] - (b[key] as number);
      case 'string':
        return a[key].localeCompare(b[key] as string);
      default:
        return 0;
    }
  });
}

export function distinct<T extends object, K extends keyof T>(array: T[], key: K): T[K][] {
  if (!array?.length) return [];
  return [...new Set(array.map(item => item[key]))];
}

export function sort<T>(array: T[]): T[] {
  if (!array?.length) return [];
  return array.sort((a, b) => {
    if (typeof a == 'string' && typeof b == 'string') return a.localeCompare(b);
    if (typeof a == 'number' && typeof b == 'number') return a - b;
    return a == null ? -1 : 1;
  });
}

export function max<T extends object, K extends KeysOfType<T, number>>(array: T[], key: K): number {
  if (!array?.length) return 0;
  const byKey = array.map(item => item[key]) as number[];
  return Math.max(...byKey);
}

export function distinctBy<T extends object>(array: T[], key: Key<T>) {
  if (!array?.length) return [];
  if (array.length == 1) return array;
  const seen = new Set<unknown>();
  return array.filter(item => {
    const value = item[key];
    if (seen.has(value)) return false;
    seen.add(value);
    return true;
  });
}

export function lookupBy<T extends object, K extends Key<T>>(array: T[], key: K): Map<T[K], T[]> {
  const map = new Map<T[K], T[]>();
  if (!array?.length) return map;

  for (const item of array) {
    const group = map.get(item[key]) ?? [];
    group.push(item);
    map.set(item[key], group);
  }
  return map;
}

export function matchBy<T, K extends Key<M>, M extends Record<K, T>>(match: T[], matchTo: M[], key: K): M[] {
  if (!match?.length) return [];
  return match.map(value => matchTo.find(item => item[key] === value) ?? ({ [key]: value } as M));
}

export function differenceBy<T extends object>(excludeFrom: T[], exclude: T[], key: Key<T>) {
  if (!excludeFrom?.length) return [];
  if (!exclude?.length) return [...excludeFrom];
  const excludeByKey = exclude.map(item => item[key]);
  return excludeFrom.filter(item => !excludeByKey.includes(item[key]));
}

export function difference<T>(excludeFrom: T[], exclude: T[]) {
  if (!excludeFrom?.length) return [];
  if (!exclude?.length) return [...excludeFrom];

  return excludeFrom.filter(item => !exclude.includes(item));
}

export function partition<T>(array: T[], callback: (element: T, index: number, array: T[]) => boolean) {
  return array.reduce(
    ({ found, rest }, element, i) => {
      callback(element, i, array) ? found.push(element) : rest.push(element);

      return { found, rest };
    },
    { found: [], rest: [] }
  );
}

export function errorHandle(er: any): string {
  console.error(er);
  if (er instanceof HttpErrorResponse) {
    switch (er.status) {
      case HttpStatusCode.BadRequest:
        return typeof er.error == 'string' ? er.error : (er.error.title as string);
      case HttpStatusCode.NotFound:
      case HttpStatusCode.MethodNotAllowed:
        return `${er.url} nicht gefunden`;
      case HttpStatusCode.InternalServerError:
        return er.error as string;
    }
  }

  return (er as Error).message;
}

export function insertAt<T>(array: T[], index: number, item: T) {
  array.splice(index, 0, item);
  return array;
}

export function insertBefore<T>(array: T[], after: T, item: unknown) {
  const index = array.indexOf(after);
  return insertAt(array, index, item);
}

export function insertBeforeBy<T>(array: T[], key: Key<T>, after: T[Key<T>], item: T) {
  const index = array.findIndex(row => row[key] == after);
  return insertAt(array, index, item);
}

export function round(value: number, pos = 2) {
  return +value.toFixed(pos);
}

export function extractKeys<T extends object, K extends Key<T>>(object: T, keys: K[]): Pick<T, K> {
  const map = {} as Pick<T, K>;

  for (const key of keys) {
    if (!(key in object)) throw new Error(`Key does not exist in extractKeys: ${key}`);
    map[key] = object[key];
  }

  return map;
}

export async function runPerformanceTest(func: Function, label: string): Promise<void> {
  const ITERATIONS = 300;

  console.log('Starte Perfomance Test for', label);
  const startFirst = performance.now();
  try {
    await func();
  } catch (e) {
    console.error(`Func ${label} fehlgeschlagen.`, e);
    return;
  }
  const endFirst = performance.now();

  const startAll = performance.now();
  for (let i = 0; i < ITERATIONS; i++) {
    await func();
  }
  const endAll = performance.now();

  const firstDurationMs = endFirst - startFirst;
  const totalDurationMs = endAll - startAll;
  const averageDurationMs = totalDurationMs / ITERATIONS;

  console.group(`BENCHMARK ERGEBNISSE for ${label}`);
  console.log(
    `Gesamtzeit ${ITERATIONS} Läufe: `,
    `${totalDurationMs.toFixed(2)} ms (${(totalDurationMs / 1000).toFixed(2)} s)`
  );
  console.log(`Schnitt pro Lauf: `, `${averageDurationMs.toFixed(4)} ms`);
  console.log(`Zeit erstes Laufs: `, `${firstDurationMs.toFixed(2)} ms`);
  console.groupEnd();
}
